// src/pages/KitchenMode.js
//
// Kitchen Mode — Part 2.
// Layout-first flow: name the kitchen → pick a layout (L / U) → measure each
// wall by photo (tap two ends) via the focused KitchenWallMeasure screen, which
// returns { lengthFt, heightFt } back to the wall slot.
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../services/firebase";
import KitchenWallMeasure from "./KitchenWallMeasure";
import "./KitchenMode.css";
import Kitchen3DView from "../components/kitchen/Kitchen3DView";

const LAYOUTS = {
  "l-shape": {
    key: "l-shape",
    label: "L-shape",
    blurb: "Two walls meeting at a corner.",
    slots: [
      { role: "back", label: "Back wall" },
      { role: "side", label: "Side wall" },
    ],
  },
  "u-shape": {
    key: "u-shape",
    label: "U-shape",
    blurb: "A back wall with a wall on each side.",
    slots: [
      { role: "left", label: "Left wall" },
      { role: "back", label: "Back wall" },
      { role: "right", label: "Right wall" },
    ],
  },
};

const Card = ({ title, children, right }) => (
  <div className="km-card">
    {(title || right) && (
      <div className="km-card-header">
        <span className="km-card-title">{title}</span>
        {right}
      </div>
    )}
    {children}
  </div>
);

const feetInchesLabel = (feet) => {
  if (feet == null || !isFinite(feet)) return "—";
  const totalIn = Math.round(feet * 12);
  const ft = Math.floor(totalIn / 12);
  const inch = totalIn % 12;
  if (ft === 0 && inch === 0) return "0\u2033";
  if (inch === 0) return `${ft}\u2032`;
  if (ft === 0) return `${inch}\u2033`;
  return `${ft}\u2032 ${inch}\u2033`;
};

export default function KitchenMode() {
  const navigate = useNavigate();

  const [kitchenName, setKitchenName] = useState("");
  const [nameError, setNameError] = useState(null);
  const [layoutKey, setLayoutKey] = useState(null);
  const [walls, setWalls] = useState({});
  const [measuring, setMeasuring] = useState(null); // { role, label } | null
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const layout = layoutKey ? LAYOUTS[layoutKey] : null;

  const pickLayout = (key) => {
    setLayoutKey(key);
    setWalls((prev) => {
      const allowed = new Set(LAYOUTS[key].slots.map((s) => s.role));
      const next = {};
      for (const role of Object.keys(prev)) if (allowed.has(role)) next[role] = prev[role];
      return next;
    });
  };

  const measuredCount = layout
    ? layout.slots.filter((s) => walls[s.role]?.lengthFt != null).length
    : 0;

    // A 3D model is generated only after every wall required
    // by the selected layout has been measured.
  const layoutComplete =
  !!layout &&
  measuredCount === layout.slots.length &&
  measuredCount > 0;

  const summary = useMemo(() => {
    if (!layout) return null;
    const roles = layout.slots.map((s) => s.role);
    const lengths = roles.map((r) => walls[r]?.lengthFt).filter((n) => n != null && isFinite(n));
    const totalLinearFt = lengths.reduce((a, b) => a + b, 0);
    let wallAreaSqFt = 0;
    let anyHeight = false;
    for (const r of roles) {
      const w = walls[r];
      if (w?.lengthFt != null && w?.heightFt != null) {
        wallAreaSqFt += w.lengthFt * w.heightFt;
        anyHeight = true;
      }
    }
    return {
      totalLinearFt,
      wallAreaSqFt: anyHeight ? wallAreaSqFt : null,
      wallsMeasured: measuredCount,
      wallsExpected: layout.slots.length,
    };
  }, [layout, walls, measuredCount]);

  const requireName = () => {
    if (!kitchenName.trim()) {
      setNameError("Kitchen name is required.");
      return false;
    }
    setNameError(null);
    return true;
  };

  // Part 3a — persist the kitchen to Firestore `projects`, matching the same
  // lifecycle schema as multiroom (so it flows into the project pipeline),
  // with a kitchen-specific payload. Images (wall photos) come in Piece 2.
  const saveKitchen = async () => {
    if (!requireName()) return;
    if (!layout || measuredCount === 0) return;
    const user = auth.currentUser;
    if (!user) {
      setSaveError("You must be signed in to save.");
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const projectId =
        (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
        `kitchen_${Date.now()}`;

      // Serialize measured walls in slot order (only those with a length).
      const wallsPayload = {};
      layout.slots.forEach((slot) => {
        const w = walls[slot.role];
        if (w?.lengthFt != null) {
          wallsPayload[slot.role] = {
            role: slot.role,
            label: slot.label,
            lengthFt: w.lengthFt,
            heightFt: w.heightFt ?? null,
            preview: w.preview ?? null,
          };
        }
      });

      const project = {
        id: projectId,
        projectName: kitchenName.trim(),
        createdAt: serverTimestamp(),
        ownerUid: user.uid,
        ownerEmail: user.email || null,
        // lifecycle fields (match multiroom so it slots into the pipeline)
        projectType: "kitchen",
        mode: "kitchen",
        status: "private",
        sharedWithContractor: false,
        sharedAt: null,
        // kitchen payload
        layout: layout.key,
        layoutLabel: layout.label,
        walls: wallsPayload,
        summary: {
          totalLinearFt: summary?.totalLinearFt ?? 0,
          wallAreaSqFt: summary?.wallAreaSqFt ?? null,
          wallsMeasured: summary?.wallsMeasured ?? 0,
          wallsExpected: summary?.wallsExpected ?? layout.slots.length,
        },
        // harmless empty default so any multiroom `rooms` reader won't crash
        rooms: [],
      };

      await setDoc(doc(db, "projects", projectId), project);
      // For now, confirm + return to the mode hub. The kitchen dashboard/detail
      // views (Part 3b/3c) will become the natural destination.
      alert(`Saved "${project.projectName}". You can view it in your projects.`);
      navigate("/measure");
    } catch (err) {
      console.error("Kitchen save failed:", err);
      setSaveError("Couldn't save the kitchen. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="km-root">
      <div className="km-head">
        <h1 className="km-title">Kitchen Mode</h1>
        <p className="km-subtitle">
          Measure each wall, generate the kitchen geometry, and create a
          remote 3D site survey — no initial site visit needed.
        </p>
      </div>

      {measuring ? (
        <div className="km-body">
          <KitchenWallMeasure
            wallLabel={measuring.label}
            onCancel={() => setMeasuring(null)}
            onComplete={(result) => {
              setWalls((prev) => ({
                ...prev,
                [measuring.role]: {
                  lengthFt: result.lengthFt,
                  heightFt: result.heightFt,
                  preview: result.preview || null,
                },
              }));
              setMeasuring(null);
            }}
          />
        </div>
      ) : (
        <div className="km-body">
          {/* Kitchen name */}
          <Card title="Kitchen">
            <div className="km-field">
              <label className="km-label">
                Kitchen name <span className="km-req">*</span>
              </label>
              <input
                className={`km-input ${nameError ? "km-input--error" : ""}`}
                value={kitchenName}
                onChange={(e) => {
                  setKitchenName(e.target.value);
                  if (nameError && e.target.value.trim()) setNameError(null);
                }}
                placeholder="e.g. Smith kitchen remodel"
              />
              {nameError && <p className="km-field-error">{nameError}</p>}
            </div>
          </Card>

          {/* Layout picker */}
          <Card title="Choose a layout">
            <div className="km-layout-grid">
              {Object.values(LAYOUTS).map((l) => (
                <button
                  key={l.key}
                  type="button"
                  className={`km-layout-card ${layoutKey === l.key ? "km-layout-card--active" : ""}`}
                  onClick={() => pickLayout(l.key)}
                >
                  <span className="km-layout-name">{l.label}</span>
                  <span className="km-layout-blurb">{l.blurb}</span>
                  <span className="km-layout-count">{l.slots.length} walls</span>
                </button>
              ))}
            </div>
          </Card>

          {/* Wall slots */}
          {layout && (
            <Card
              title="Measure the walls"
              right={
                <span className="km-progress">
                  {measuredCount} of {layout.slots.length} measured
                </span>
              }
            >
              <div className="km-walls">
                {layout.slots.map((slot) => {
                  const w = walls[slot.role] || {};
                  const done = w.lengthFt != null;
                  return (
                    <div key={slot.role} className={`km-wall ${done ? "km-wall--done" : ""}`}>
                      <div className="km-wall-head">
                        <span className="km-wall-role">{slot.label}</span>
                        <span className={`km-wall-status ${done ? "is-done" : ""}`}>
                          {done ? `\u2713 ${feetInchesLabel(w.lengthFt)}` : "Not measured"}
                        </span>
                      </div>

                      {done ? (
                        <div className="km-wall-result">
                          {w.preview && (
                            <img src={w.preview} alt={`${slot.label} preview`} className="km-wall-thumb" />
                          )}
                          <div className="km-wall-dims">
                            <span>Length: <strong>{feetInchesLabel(w.lengthFt)}</strong></span>
                            {w.heightFt != null && (
                              <span> · Height: <strong>{feetInchesLabel(w.heightFt)}</strong></span>
                            )}
                          </div>
                          <button
                            className="km-btn km-btn--ghost km-btn--sm"
                            onClick={() => setMeasuring({ role: slot.role, label: slot.label })}
                          >
                            Re-measure
                          </button>
                        </div>
                      ) : (
                        <button
                          className="km-btn km-btn--primary km-measure-btn"
                          onClick={() => setMeasuring({ role: slot.role, label: slot.label })}
                        >
                          Measure this wall
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Summary */}
          {layout && summary && (
            <Card title="Kitchen summary">
              <div className="km-metrics">
                <div className="km-metric">
                  <div className="km-metric-label">Layout</div>
                  <div className="km-metric-value">{layout.label}</div>
                </div>
                <div className="km-metric">
                  <div className="km-metric-label">Walls measured</div>
                  <div className="km-metric-value">
                    {summary.wallsMeasured} / {summary.wallsExpected}
                  </div>
                </div>
                <div className="km-metric">
                  <div className="km-metric-label">Total run</div>
                  <div className="km-metric-value">
                    {summary.totalLinearFt > 0 ? `${summary.totalLinearFt.toFixed(1)} ft` : "—"}
                  </div>
                </div>
                <div className="km-metric">
                  <div className="km-metric-label">Wall area</div>
                  <div className="km-metric-value">
                    {summary.wallAreaSqFt != null ? `${summary.wallAreaSqFt.toFixed(1)} sq ft` : "—"}
                  </div>
                </div>
              </div>
              {summary.wallsMeasured < summary.wallsExpected && (
                <p className="km-summary-hint">
                  {layout.label} needs {summary.wallsExpected} walls —{" "}
                  {summary.wallsExpected - summary.wallsMeasured} still to measure. The layout diagram
                  draws once the walls are in.
                </p>
              )}
            </Card>
          )}

	  {/* 3D Remote Site Survey */}
          {layoutComplete && (
            <Card
              title="3D Remote Site Survey"
              right={
                <span className="km-progress">
                  Interactive view
                </span>
              }
            >
              <p
                className="km-summary-hint"
                style={{ marginTop: 0 }}
              >
                Generated directly from your measured wall dimensions.
                Drag to rotate, scroll to zoom, and right-click to pan.
              </p>

              <Kitchen3DView
                layout={layout.key}
                walls={walls}
              />
            </Card>
          )}

          {/* Actions */}
          <div className="km-actions">
            <button className="km-btn km-btn--ghost" onClick={() => navigate("/measure")}>
              ← Back to modes
            </button>
            <button
              className="km-btn km-btn--primary"
              disabled={!layout || measuredCount === 0 || saving}
              onClick={saveKitchen}
            >
              {saving ? "Saving…" : "Save kitchen"}
            </button>
          </div>
          {saveError && <p className="km-field-error" style={{ textAlign: "right" }}>{saveError}</p>}
        </div>
      )}
    </div>
  );
}
